/* eslint-disable import/no-extraneous-dependencies */
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  GenericError,
  Header,
  Text,
  Button,
  Icon,
  NewButton,
  Tooltip,
  TimeAgo,
  ViewOnlyButton
} from '../../../components';

import {
  AssetModal,
  AssetCardSkeleton,
  NoAssets,
  NoAssetsReadOnly,
  SpaceHeaderDropdown,
  LeaveSpaceModal,
  DeleteSpaceModal,
  SortableAssetCard,
  DragAndDropContainer,
  SortablePlaceholder,
  SchedulesDropdown,
  CustomScheduleModal
} from '../components';

import {
  useConversations,
  useQuestions,
  useSpaces,
  useTermTranslations,
  useAnswers,
  useSchedules
} from '../providers';
import { usePortal } from '../../../providers';
import { useSources, useUsers } from '../../organization';

// Styled components for layout
const AssetsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  box-sizing: border-box;
  padding: 16px 24px;
  max-height: calc(100vh - 77px); /* 77px is the height of the header */
  overflow-y: ${({ $isLoading }) => ($isLoading ? 'hidden' : 'scroll')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlaceholderCard = styled.div`
  height: 100%;
  min-height: 200px;
  width: 100%;
`;

const RefreshText = styled(Text)`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const RefreshButton = styled(Button)`
  padding: 6px 8px;
  border-radius: 8px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  height: 36px;
  margin-left: -1px;

  margin-left: 8px;
  border: ${({ $isLessThan24HoursAgo }) =>
    $isLessThan24HoursAgo ? '1px solid var(--border-muted)' : '1px solid transparent'};
  border-right: none !important;
`;

const RefreshScheduleButton = styled(Button)`
  border-radius: 8px;
  padding: 8px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  height: 36px;
  border: ${({ $isLessThan24HoursAgo }) =>
    $isLessThan24HoursAgo ? '1px solid var(--border-muted)' : '1px solid transparent'};
  border-left: ${({ $isLessThan24HoursAgo }) =>
    $isLessThan24HoursAgo
      ? '1px solid var(--border-muted) !important'
      : '1px solid var(--white) !important'};
`;

const RefreshActionsContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Space = () => {
  // Refs and hooks for navigation and state management
  const assetListRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const { showPortal } = usePortal();

  // Custom hooks for data management
  const {
    assets,
    removeQuestionFromSpace,
    addQuestionToSpace,
    updateQuestion,
    isRefetchingAssets,
    isLoadingVersions,
    selectedAsset,
    selectedAssetVersionsQuery,
    retryAssetQuestion
  } = useQuestions();

  const { currentUser } = useUsers();
  const { updateAnswer } = useAnswers();
  const { createConversation } = useConversations();
  const { spaces, createSpace } = useSpaces();
  const { sources } = useSources();
  const { termTranslations } = useTermTranslations();
  const { currentSpace, refreshSpace, updateSpace, deleteSpace, leaveSpace, handleShare } =
    useSpaces();
  const { createSpaceRefreshSchedule, updateSpaceRefreshSchedule } = useSchedules();

  const handleColumnOrderChange = useCallback((newColumnOrder, id) => {
    updateAnswer(id, { columnOrder: newColumnOrder });
  }, []);

  // Maintain two-column state. Keys "left" and "right" each hold an array of asset objects.
  // State for DND functionality
  const [columns, setColumns] = useState({ left: [], right: [] });
  const [activeId, setActiveId] = useState(null);

  // Load assets into columns when they change
  useEffect(() => {
    if (assets) {
      // Sort assets by order first
      const sortedAssets = [...assets].sort((a, b) => {
        if (a.order === undefined) return 1;
        if (b.order === undefined) return -1;
        return a.order - b.order;
      });

      // Find the maximum order number to avoid duplicates
      const maxOrder = Math.max(...sortedAssets.map((asset) => asset.order || 0), 0);
      let nextOrder = maxOrder + 1;

      // Split into columns based on order (odd/even)
      const leftColumn = sortedAssets
        .filter((asset) => asset.order !== null && asset.order % 2 === 0)
        .map((asset) => asset);
      const rightColumn = sortedAssets
        .filter((asset) => asset.order !== null && asset.order % 2 !== 0)
        .map((asset) => asset);

      // Handle items with null orders
      const itemsWithNullOrder = sortedAssets.filter((asset) => asset.order === null);
      itemsWithNullOrder.forEach((item, index) => {
        if (index % 2 === 0) {
          item.set('order', nextOrder);
          leftColumn.push(item);
          nextOrder += 2;
        } else {
          item.set('order', nextOrder);
          rightColumn.push(item);
          nextOrder += 2;
        }
      });

      setColumns({
        left: leftColumn,
        right: rightColumn
      });
    }
  }, [assets]);

  // Event handlers for space management
  const handleLeaveSpace = useCallback(() => {
    showPortal(
      <LeaveSpaceModal
        title={currentSpace.title}
        onLeaveSpace={() => leaveSpace(currentSpace.id)}
      />
    );
  }, [currentSpace]);

  const handleDeleteSpace = useCallback(() => {
    showPortal(
      <DeleteSpaceModal
        title={currentSpace.title}
        onDeleteSpace={() => deleteSpace(currentSpace.id)}
      />
    );
  }, [currentSpace]);

  // Event handlers for asset management
  const handleUpdateAssetTitle = useCallback(
    async (question, assetTitle) => {
      await updateQuestion({ body: { assetTitle }, action: 'update_asset_title' }, question.id);
    },
    [updateQuestion]
  );

  const handleUpdateTitle = useCallback(
    (title) => {
      updateSpace(spaceId, { title });
      navigate(location.pathname, { replace: true, state: null });
    },
    [location.state, spaceId]
  );

  const handleStartChat = useCallback(
    async (question) => {
      const conversation = await createConversation('New conversation', question.id);
      navigate(`/chats/${conversation.id}`);
    },
    [createConversation, navigate]
  );

  const handleRemoveAssetFromSpace = useCallback(
    (question) => removeQuestionFromSpace(question, currentSpace),
    [currentSpace]
  );

  // Handle asset modal display based on URL hash
  useEffect(() => {
    if (location.hash && location.hash !== '#' && selectedAsset) {
      showPortal(
        <AssetModal
          onClose={() => navigate('', { replace: true })}
          onStartChat={() => handleStartChat(selectedAsset)}
          onCreateSpace={createSpace}
          onAssetTitleChange={handleUpdateAssetTitle}
          onAddQuestionToSpace={addQuestionToSpace}
          onRemoveQuestionFromSpace={removeQuestionFromSpace}
          navigate={navigate}
          retryQuestion={retryAssetQuestion}
          question={selectedAsset}
          sources={sources}
          spaces={spaces}
          termTranslations={termTranslations}
          isReadOnly={!currentUser?.canAsk}
          handleColumnOrderChange={handleColumnOrderChange}
          fetchNextPage={selectedAssetVersionsQuery.fetchNextPage}
          hasNextPage={selectedAssetVersionsQuery.hasNextPage}
          areVersionsLoading={isLoadingVersions(selectedAsset.id)}
        />
      );
    }
  }, [
    location.hash,
    selectedAsset?.status, // React to status changes (retry states)
    selectedAsset?.answer?.id, // React to answer changes
    selectedAsset?.assetTitle, // React to title changes
    selectedAsset?.isRetry // React to retry flag changes
  ]);

  const containsFailedRefreshes = useMemo(
    () => assets?.some((asset) => asset.hasFailedNewVersions),
    [assets]
  );

  const isLessThan24HoursAgo = useMemo(() => {
    if (!currentSpace.updatedAt) return false;
    return new Date(currentSpace.updatedAt).getTime() > Date.now() - 24 * 60 * 60 * 1000;
  }, [currentSpace?.updatedAt]);

  const isSavedAssets = useMemo(
    () => currentSpace.title.toLowerCase() === 'saved assets',
    [currentSpace.title]
  );

  const onScheduleClick = useCallback(
    (data) => {
      if (currentSpace.spaceRefreshSchedule) {
        updateSpaceRefreshSchedule({
          spaceId: currentSpace.id,
          spaceName: currentSpace.title,
          spaceRefreshScheduleId: currentSpace.spaceRefreshSchedule.id,
          ...data
        });
      } else {
        createSpaceRefreshSchedule({
          ...data,
          spaceId: currentSpace.id,
          spaceName: currentSpace.title
        });
      }
    },
    [currentSpace, createSpaceRefreshSchedule, updateSpaceRefreshSchedule]
  );

  const onCustomScheduleClick = useCallback(
    () => showPortal(<CustomScheduleModal onSubmitConversationSchedule={onScheduleClick} />),
    [showPortal, onScheduleClick]
  );

  return (
    <>
      {currentSpace && (
        <Header
          title={currentSpace.title}
          onUpdateTitle={handleUpdateTitle}
          editTitle={location.state?.editTitle}
          isReadOnly={!currentUser?.canAsk || isSavedAssets}
          actions={
            currentUser?.canAsk && !isSavedAssets ? (
              <>
                {currentSpace.updatedAt && refreshSpace && (
                  <>
                    <RefreshText as='div'>
                      {containsFailedRefreshes && (
                        <Tooltip placement='bottom-start' content={`Some assets couldn't refresh`}>
                          <Icon name='alert' displaySize={20} />
                        </Tooltip>
                      )}
                      Last refreshed <TimeAgo timestamp={currentSpace.updatedAt} label='on' />
                    </RefreshText>
                    <RefreshActionsContainer>
                      <RefreshButton
                        onClick={() => refreshSpace(spaceId)}
                        naked={isLessThan24HoursAgo}
                        primary={!isLessThan24HoursAgo}
                        $isLessThan24HoursAgo={isLessThan24HoursAgo}
                      >
                        <Icon
                          size={20}
                          name='refresh'
                          color={isLessThan24HoursAgo ? 'var(--black)' : 'var(--white)'}
                        />
                        Refresh
                      </RefreshButton>
                      <SchedulesDropdown
                        currentSchedule={currentSpace?.spaceRefreshSchedule}
                        onScheduleClick={(scheduleParams) => {
                          onScheduleClick(scheduleParams);
                        }}
                        onCustomScheduleClick={onCustomScheduleClick}
                        toggleComponent={
                          <RefreshScheduleButton
                            naked={isLessThan24HoursAgo}
                            primary={!isLessThan24HoursAgo}
                            $isLessThan24HoursAgo={isLessThan24HoursAgo}
                          >
                            <Icon
                              name='chevron-down'
                              color={isLessThan24HoursAgo ? 'var(--black)' : 'var(--white)'}
                              displaySize={20}
                            />
                          </RefreshScheduleButton>
                        }
                      />
                    </RefreshActionsContainer>
                  </>
                )}
                <NewButton tertiary onClick={handleShare}>
                  <Icon name='user-plus' />
                </NewButton>
                <SpaceHeaderDropdown
                  onLeaveSpace={currentSpace.isSharedWith(currentUser.id) ? handleLeaveSpace : null}
                  onDeleteSpace={
                    currentSpace.isSharedWith(currentUser.id) ? null : handleDeleteSpace
                  }
                />
              </>
            ) : (
              !isSavedAssets && <ViewOnlyButton />
            )
          }
        />
      )}

      {!isRefetchingAssets &&
        assets?.length === 0 &&
        (!currentUser?.canAsk ? (
          <NoAssetsReadOnly />
        ) : (
          <NoAssets onCreateChatClick={() => navigate('/chats/new')} />
        ))}

      <ErrorBoundary fallback={<GenericError />}>
        <DragAndDropContainer
          columns={columns}
          setColumns={setColumns}
          currentUser={currentUser}
          activeId={activeId}
          setActiveId={setActiveId}
        >
          <AssetsList ref={assetListRef}>
            <Container id='left'>
              <SortableContext
                items={[...columns.left.map((item) => item.id), 'left-placeholder']}
                strategy={verticalListSortingStrategy}
              >
                {columns.left.map((question, index) =>
                  isLoadingVersions(question.id) ? (
                    <AssetCardSkeleton key={`left-${index}`} />
                  ) : (
                    <SortableAssetCard
                      key={question.id}
                      question={question}
                      onRemove={handleRemoveAssetFromSpace}
                      retryQuestion={retryAssetQuestion}
                      onClick={() => navigate(`#${question.id}`, { replace: true })}
                      isReadOnly={!currentUser?.canAsk}
                      containerId='left'
                      sources={sources}
                    />
                  )
                )}
                {columns.left.length === 0 && (
                  <SortablePlaceholder id='left-placeholder' containerId='left'>
                    <PlaceholderCard />
                  </SortablePlaceholder>
                )}
              </SortableContext>
            </Container>
            <Container id='right'>
              <SortableContext
                items={[...columns.right.map((item) => item.id), 'right-placeholder']}
                strategy={verticalListSortingStrategy}
              >
                {columns.right.map((question, index) =>
                  isLoadingVersions(question.id) ? (
                    <AssetCardSkeleton key={`right-${index}`} />
                  ) : (
                    <SortableAssetCard
                      key={question.id}
                      question={question}
                      onRemove={handleRemoveAssetFromSpace}
                      retryQuestion={retryAssetQuestion}
                      onClick={() => navigate(`#${question.id}`, { replace: true })}
                      isReadOnly={!currentUser?.canAsk}
                      containerId='right'
                      sources={sources}
                    />
                  )
                )}
                {columns.right.length === 0 && (
                  <SortablePlaceholder id='right-placeholder' containerId='right'>
                    <PlaceholderCard />
                  </SortablePlaceholder>
                )}
              </SortableContext>
            </Container>
          </AssetsList>
        </DragAndDropContainer>
      </ErrorBoundary>
    </>
  );
};

export { Space };
